import type { AvatarConfig, ClientConfig, MapData, SessionProfile, UmgebungSummary } from '@etown/shared';

/**
 * Schlanker Wrapper um die HTTP-API. Alle Antworten folgen dem Envelope
 * { ok, data | error }; Fehler werden als Error mit Server-Meldung geworfen.
 * Verwaltungs-Aufrufe liegen in adminApi.ts.
 */

/** Das angemeldete Konto samt seiner Umgebungen (Antwort von login/me). */
export type SessionUser = SessionProfile;

/** Minimaler Nutzer-Eintrag fuer den DM-Empfaenger-Picker. */
export interface DirectoryUser {
  id: string;
  displayName: string;
  isActive: boolean;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    ...init,
  });
  const body = (await res.json().catch(() => null)) as { ok: boolean; data?: T; error?: string } | null;
  if (!res.ok || !body?.ok) {
    throw new Error(body?.error ?? `Anfrage fehlgeschlagen (${res.status})`);
  }
  return body.data as T;
}

export const api = {
  login(username: string, password: string): Promise<{ user: SessionUser }> {
    return request('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
  },
  logout(): Promise<null> {
    return request('/api/auth/logout', { method: 'POST' });
  },
  me(): Promise<{ user: SessionUser }> {
    return request('/api/auth/me');
  },
  umgebungen(): Promise<{ umgebungen: UmgebungSummary[] }> {
    return request('/api/umgebungen');
  },
  config(): Promise<ClientConfig> {
    return request('/api/config');
  },
  map(id: string): Promise<{ map: MapData; version: number }> {
    return request(`/api/maps/${encodeURIComponent(id)}`);
  },
  saveMap(map: MapData): Promise<{ version: number }> {
    return request(`/api/maps/${encodeURIComponent(map.id)}`, { method: 'PUT', body: JSON.stringify(map) });
  },
  chatHistory(mapId: string, beforeId?: number): Promise<{ messages: import('@etown/shared').ChatMessage[]; hasMore: boolean }> {
    const query = beforeId !== undefined ? `?before=${beforeId}` : '';
    return request(`/api/maps/${encodeURIComponent(mapId)}/chat${query}`);
  },
  saveAvatar(avatar: AvatarConfig): Promise<null> {
    return request('/api/users/me/avatar', { method: 'PUT', body: JSON.stringify(avatar) });
  },
  listUsers(): Promise<{ users: DirectoryUser[] }> {
    return request('/api/users');
  },
  async uploadObjectImage(mapId: string, objectId: string, file: File): Promise<{ imageId: string }> {
    const res = await fetch(
      `/api/maps/${encodeURIComponent(mapId)}/objects/${encodeURIComponent(objectId)}/image`,
      { method: 'POST', headers: { 'Content-Type': file.type }, credentials: 'same-origin', body: file },
    );
    const body = (await res.json().catch(() => null)) as { ok: boolean; data?: { imageId: string }; error?: string } | null;
    if (!res.ok || !body?.ok || !body.data) {
      throw new Error(body?.error ?? `Upload fehlgeschlagen (${res.status})`);
    }
    return body.data;
  },
};
